import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCart, removeFromCart, clearCart, saveCart } from '../firebase/config';
// Import Firebase directly for database operations
import { getDatabase, ref, get } from 'firebase/database';
import './Pages.css';

// Import Material-UI Icons
import WhatshotIcon from '@mui/icons-material/Whatshot';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ComputerIcon from '@mui/icons-material/Computer';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import QuizIcon from '@mui/icons-material/Quiz';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import BadgeIcon from '@mui/icons-material/Badge';
import StarIcon from '@mui/icons-material/Star';
import CodeIcon from '@mui/icons-material/Code';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FolderIcon from '@mui/icons-material/Folder';

// Map icon names to Material-UI components
var iconMap = {
  'whatshot': WhatshotIcon,
  'public': PublicIcon,
  'language': LanguageIcon,
  'groups': GroupsIcon,
  'person': PersonIcon,
  'menu_book': MenuBookIcon,
  'linkedin': LinkedInIcon,
  'computer': ComputerIcon,
  'verified': VerifiedIcon,
  'school': SchoolIcon,
  'fact_check': FactCheckIcon,
  'quiz': QuizIcon,
  'psychology': PsychologyIcon,
  'description': DescriptionIcon,
  'checklist': ChecklistIcon,
  'schedule': ScheduleIcon,
  'rocket_launch': RocketLaunchIcon,
  'search': SearchIcon,
  'grid_view': GridViewIcon,
  'how_to_reg': HowToRegIcon,
  'badge': BadgeIcon,
  'star': StarIcon,
  'code': CodeIcon,
  'receipt': ReceiptIcon,
};

var getIconComponent = function(iconName) {
  return iconMap[iconName] || FolderIcon;
};

// Helper function to dispatch cart update event
var dispatchCartUpdate = function(userId, isLoggedIn, cartData) {
  var cartUpdateEvent = new CustomEvent('cartUpdated', {
    detail: {
      userId: userId,
      isLoggedIn: isLoggedIn,
      cartData: cartData
    }
  });
  window.dispatchEvent(cartUpdateEvent);
};

var Cart = function() {
  var navigate = useNavigate();
  var [cartItems, setCartItems] = useState([]);
  var [referenceCode, setReferenceCode] = useState('');
  var [paymentMode, setPaymentMode] = useState('1');
  var [loading, setLoading] = useState(true);
  var [currentUser, setCurrentUser] = useState(null);
  var [referralError, setReferralError] = useState('');
  var [referralValid, setReferralValid] = useState(false);
  var [referralUser, setReferralUser] = useState(null);
  var [checkingReferral, setCheckingReferral] = useState(false);

  useEffect(function() {
    // Get current user from localStorage
    var user = localStorage.getItem('currentUser');
    if (user) {
      try {
        var parsedUser = JSON.parse(user);
        setCurrentUser(parsedUser);
        loadCart(parsedUser.userId);
      } catch (e) {
        loadCart(null);
      }
    } else {
      loadCart(null);
    }
  }, []);

  var loadCart = async function(userId) {
    setLoading(true);
    if (userId) {
      var result = await getCart(userId);
      if (result.success) {
        setCartItems(result.data || []);
      }
    } else {
      var savedCart = localStorage.getItem('cartItems');
      if (savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch (e) {
          setCartItems([]);
        }
      }
    }
    setLoading(false);
  };

  // Validate Referral Code - Check both user-specific and universal codes
  var validateReferralCode = async function(code) {
    // If code is empty, show error
    if (!code || code.trim() === '') {
      setReferralError('❌ Referral code is required.');
      setReferralValid(false);
      setReferralUser(null);
      return;
    }

    setCheckingReferral(true);
    setReferralError('');
    setReferralValid(false);
    setReferralUser(null);

    try {
      var db = getDatabase();
      
      // First, check if it's the universal referral code
      var universalRef = ref(db, 'admin/universalReferralCode');
      var universalSnapshot = await get(universalRef);
      
      if (universalSnapshot.exists()) {
        var universalCode = universalSnapshot.val();
        if (universalCode.toUpperCase() === code.toUpperCase()) {
          // It's the universal code - valid for all users
          setReferralValid(true);
          setReferralUser({
            firstName: 'Universal',
            lastName: 'Referral',
            isUniversal: true
          });
          setReferralError('');
          setCheckingReferral(false);
          return;
        }
      }
      
      // If not universal, check user-specific referral codes
      var snapshot = await get(ref(db, 'users'));
      
      if (snapshot.exists()) {
        var users = snapshot.val();
        var foundUser = null;
        var foundUserId = null;
        
        // Search for user with this referral code
        for (var userId in users) {
          if (users.hasOwnProperty(userId)) {
            var userData = users[userId];
            if (userData.referralCode && userData.referralCode.toUpperCase() === code.toUpperCase()) {
              foundUser = userData;
              foundUserId = userId;
              break;
            }
          }
        }
        
        if (foundUser) {
          // Allow users to use their own referral code
          setReferralValid(true);
          setReferralUser(foundUser);
          setReferralError('');
        } else {
          setReferralValid(false);
          setReferralUser(null);
          setReferralError('❌ Invalid referral code. Please check and try again.');
        }
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralError('Error validating referral code');
    } finally {
      setCheckingReferral(false);
    }
  };

  // Handle referral code input change
  var handleReferralChange = function(e) {
    var code = e.target.value;
    setReferenceCode(code);
    // Validate with debounce
    clearTimeout(window.referralTimeout);
    window.referralTimeout = setTimeout(function() {
      validateReferralCode(code);
    }, 500);
  };

  // Calculate totals
  var totalAmount = cartItems.reduce(function(sum, item) { return sum + item.total; }, 0);

  var handleNavigation = function(path) {
    navigate(path);
  };

  var handleRemoveItem = async function(id) {
    var confirmDelete = window.confirm('Are you sure to delete permanently?');
    if (confirmDelete) {
      var updatedItems = [];
      
      if (currentUser) {
        var result = await removeFromCart(currentUser.userId, id);
        if (result.success) {
          updatedItems = result.data;
          setCartItems(updatedItems);
        }
      } else {
        updatedItems = cartItems.filter(function(item) { return item.id !== id; });
        setCartItems(updatedItems);
        localStorage.setItem('cartItems', JSON.stringify(updatedItems));
      }
      
      // Dispatch cart update event to update Header badge
      dispatchCartUpdate(
        currentUser ? currentUser.userId : null,
        !!currentUser,
        updatedItems
      );
    }
  };

  // Update cart count when items change
  useEffect(function() {
    if (cartItems.length >= 0) {
      // Dispatch event when cart items change (e.g., after load or removal)
      var userId = currentUser ? currentUser.userId : null;
      var isLoggedIn = !!currentUser;
      
      dispatchCartUpdate(userId, isLoggedIn, cartItems);
    }
  }, [cartItems, currentUser]);

  // UPDATED: Handle checkout with payment mode
  var handleCheckout = async function() {
    if (cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Check if referral code is empty
    if (!referenceCode || referenceCode.trim() === '') {
      setReferralError('❌ Referral code is required to proceed.');
      alert('Please enter a referral code to proceed with checkout.');
      return;
    }

    // Check if referral is valid
    if (!referralValid) {
      setReferralError('❌ Invalid referral code. Please check and try again.');
      alert('Invalid referral code. Please check and try again.');
      return;
    }

    // Save cart items to localStorage for payment page
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
    localStorage.setItem('orderTotal', JSON.stringify(totalAmount));
    localStorage.setItem('referenceCode', referenceCode || '');
    localStorage.setItem('paymentMode', paymentMode || '1'); // IMPORTANT: Save payment mode
    localStorage.setItem('referralValid', JSON.stringify(referralValid));
    if (referralUser) {
      localStorage.setItem('referralUserId', referralUser.id || '');
      localStorage.setItem('referralIsUniversal', JSON.stringify(referralUser.isUniversal || false));
    }

    // Navigate to payment page
    navigate('/payment');
  };

  var isLoggedIn = function() {
    return currentUser && currentUser.isLoggedIn;
  };

  // Check if checkout should be disabled
  var isCheckoutDisabled = function() {
    // If no referral code entered or referral is invalid
    if (!referenceCode || referenceCode.trim() === '') {
      return true;
    }
    if (!referralValid) {
      return true;
    }
    return false;
  };

  // Render Material-UI icon in cart
  var renderCartIcon = function(item) {
    var IconComponent = getIconComponent(item.image);
    var categoryColors = {
      'Career Blaze': '#FF6B6B',
      'Improve Profile': '#4ECDC4',
      'Profile Verification': '#45B7D1',
      'Career Assistance': '#96CEB4',
      'Maximize Reach': '#FFEAA7',
      'Profile ID': '#DDA0DD'
    };
    var color = categoryColors[item.category] || '#65d2bc';
    
    return React.createElement(IconComponent, { 
      style: { fontSize: '32px', color: color }
    });
  };

  if (loading) {
    return React.createElement(
      'main',
      { className: 'page-content' },
      React.createElement(
        'div',
        { className: 'container', style: { textAlign: 'center', padding: '60px 0' } },
        React.createElement('i', { className: 'fas fa-spinner fa-spin', style: { fontSize: '48px', color: '#65d2bc' } }),
        React.createElement('p', null, 'Loading cart...')
      )
    );
  }

  // If user is not logged in, show login/register prompt
  if (!isLoggedIn()) {
    return React.createElement(
      'main',
      { className: 'page-content' },
      React.createElement(
        'div',
        { className: 'container' },
        React.createElement(
          'div',
          { className: 'page-header-cart' },
          React.createElement(
            'div',
            { className: 'breadcrumb-cart' },
            React.createElement(
              'a',
              { 
                href: '#',
                onClick: function(e) { e.preventDefault(); handleNavigation('/'); },
                style: { color: '#65d2bc', textDecoration: 'none', cursor: 'pointer' }
              },
              'Home'
            ),
            React.createElement('span', { className: 'separator' }, ' / '),
            React.createElement(
              'a',
              { 
                href: '#',
                onClick: function(e) { e.preventDefault(); handleNavigation('/pages'); },
                style: { color: '#65d2bc', textDecoration: 'none', cursor: 'pointer' }
              },
              'Pages'
            ),
            React.createElement('span', { className: 'separator' }, ' / '),
            React.createElement('span', { style: { color: '#0f172a', fontWeight: '600' } }, 'My Cart')
          ),
          React.createElement(
            'div',
            { className: 'page-title-cart' },
            React.createElement('h1', null, 'My Cart'),
            React.createElement('span', null, 'My Cart')
          )
        ),
        React.createElement(
          'div',
          { className: 'login-required-container' },
          React.createElement(
            'div',
            { className: 'login-required-card' },
            React.createElement('i', { className: 'fas fa-lock', style: { fontSize: '48px', color: '#65d2bc', marginBottom: '16px' } }),
            React.createElement('h2', null, 'Login Required'),
            React.createElement(
              'p',
              null,
              'Please login or register to view your cart and proceed with checkout.'
            ),
            React.createElement(
              'div',
              { className: 'login-required-actions' },
              React.createElement(
                'button',
                {
                  className: 'btn-primary',
                  onClick: function() { handleNavigation('/login'); },
                  style: { minWidth: '160px' }
                },
                React.createElement('i', { className: 'fas fa-sign-in-alt' }),
                ' Login'
              ),
              React.createElement(
                'button',
                {
                  className: 'btn-secondary',
                  onClick: function() { handleNavigation('/register'); },
                  style: { minWidth: '160px' }
                },
                React.createElement('i', { className: 'fas fa-user-plus' }),
                ' Register'
              ),
              React.createElement(
                'button',
                {
                  className: 'btn-outline',
                  onClick: function() { handleNavigation('/'); },
                  style: { minWidth: '160px' }
                },
                React.createElement('i', { className: 'fas fa-arrow-left' }),
                ' Continue Shopping'
              )
            ),
            React.createElement(
              'div',
              { className: 'login-required-benefits' },
              React.createElement('h4', null, 'Why create an account?'),
              React.createElement(
                'ul',
                null,
                React.createElement('li', null, React.createElement('i', { className: 'fas fa-check-circle' }), ' Save your cart across devices'),
                React.createElement('li', null, React.createElement('i', { className: 'fas fa-check-circle' }), ' Track your orders'),
                React.createElement('li', null, React.createElement('i', { className: 'fas fa-check-circle' }), ' Faster checkout process'),
                React.createElement('li', null, React.createElement('i', { className: 'fas fa-check-circle' }), ' Get exclusive offers')
              )
            )
          )
        )
      )
    );
  }

  return React.createElement(
    'main',
    { className: 'page-content' },
    React.createElement(
      'div',
      { className: 'container' },
      // Page Header
      React.createElement(
        'div',
        { className: 'page-header-cart' },
        React.createElement(
          'div',
          { className: 'breadcrumb-cart' },
          React.createElement(
            'a',
            { 
              href: '#',
              onClick: function(e) { e.preventDefault(); handleNavigation('/'); },
              style: { color: '#65d2bc', textDecoration: 'none', cursor: 'pointer' }
            },
            'Home'
          ),
          React.createElement('span', { className: 'separator' }, ' / '),
          React.createElement(
            'a',
            { 
              href: '#',
              onClick: function(e) { e.preventDefault(); handleNavigation('/pages'); },
              style: { color: '#65d2bc', textDecoration: 'none', cursor: 'pointer' }
            },
            'Pages'
          ),
          React.createElement('span', { className: 'separator' }, ' / '),
          React.createElement('span', { style: { color: '#0f172a', fontWeight: '600' } }, 'My Cart')
        ),
        React.createElement(
          'div',
          { className: 'page-title-cart' },
          React.createElement('h1', null, 'My Cart'),
          React.createElement('span', null, 'My Cart')
        )
      ),

      // Cart Content
      React.createElement(
        'div',
        { className: 'shop-cart' },
        cartItems.length === 0 ? 
          React.createElement(
            'div',
            { className: 'empty-cart-message' },
            React.createElement('i', { className: 'fas fa-shopping-cart', style: { fontSize: '64px', color: '#cbd5e1' } }),
            React.createElement('h3', null, 'Your cart is empty'),
            React.createElement('p', null, 'Add some services to your cart and come back here.'),
            React.createElement(
              'button',
              { 
                className: 'btn-primary',
                onClick: function() { handleNavigation('/'); }
              },
              'Continue Shopping'
            )
          ) :
          React.createElement(
            React.Fragment,
            null,
            // Cart Table
            React.createElement(
              'div',
              { className: 'cart-table-wrapper' },
              React.createElement(
                'table',
                { className: 'cart-table' },
                React.createElement(
                  'thead',
                  null,
                  React.createElement(
                    'tr',
                    null,
                    React.createElement('th', { className: 'cart-product-remove' }, ''),
                    React.createElement('th', { className: 'cart-product-thumbnail' }, 'Product'),
                    React.createElement('th', { className: 'cart-product-name' }, 'Description'),
                    React.createElement('th', { className: 'cart-product-price' }, 'Unit Price'),
                    React.createElement('th', { className: 'cart-product-quantity' }, 'GST (18%)'),
                    React.createElement('th', { className: 'cart-product-subtotal' }, 'Total')
                  )
                ),
                React.createElement(
                  'tbody',
                  null,
                  cartItems.map(function(item) {
                    return React.createElement(
                      'tr',
                      { key: item.id },
                      React.createElement(
                        'td',
                        { className: 'cart-product-remove' },
                        React.createElement(
                          'a',
                          { 
                            href: '#',
                            onClick: function(e) { e.preventDefault(); handleRemoveItem(item.id); },
                            className: 'remove-link'
                          },
                          React.createElement('i', { className: 'fas fa-times' })
                        )
                      ),
                      // Product Thumbnail with Material Icon
                      React.createElement(
                        'td',
                        { className: 'cart-product-thumbnail' },
                        React.createElement(
                          'div',
                          { className: 'cart-thumb-wrapper' },
                          React.createElement(
                            'a',
                            { href: '#', onClick: function(e) { e.preventDefault(); } },
                            renderCartIcon(item)
                          )
                        )
                      ),
                      React.createElement(
                        'td',
                        { className: 'cart-product-description' },
                        React.createElement(
                          'p',
                          null,
                          React.createElement('span', null, item.name)
                        )
                      ),
                      React.createElement(
                        'td',
                        { className: 'cart-product-price' },
                        React.createElement('span', { className: 'amount' }, 'Rs. ' + item.price.toLocaleString())
                      ),
                      React.createElement(
                        'td',
                        { className: 'cart-product-gst' },
                        'Rs. ' + item.gst.toLocaleString()
                      ),
                      React.createElement(
                        'td',
                        { className: 'cart-product-subtotal' },
                        React.createElement('span', { className: 'amount' }, 'Rs. ' + item.total.toLocaleString())
                      )
                    );
                  })
                )
              )
            ),
            // Cart Summary
            React.createElement(
              'div',
              { className: 'cart-summary-row' },
              React.createElement(
                'div',
                { className: 'cart-summary-wrapper' },
                React.createElement('h4', null, 'Cart Total'),
                React.createElement(
                  'table',
                  { className: 'summary-table' },
                  React.createElement(
                    'tbody',
                    null,
                    React.createElement(
                      'tr',
                      null,
                      React.createElement('td', { className: 'summary-label' }, React.createElement('strong', null, 'Cart Total')),
                      React.createElement('td', { className: 'summary-value text-right' }, 
                        React.createElement('span', { className: 'amount' }, React.createElement('strong', null, 'Rs. ' + totalAmount.toLocaleString()))
                      )
                    ),
                    React.createElement(
                      'tr',
                      null,
                      React.createElement('td', { className: 'summary-label' }, React.createElement('strong', null, 'Referral Code *')),
                      React.createElement(
                        'td',
                        { className: 'summary-value text-right' },
                        React.createElement(
                          'div',
                          { className: 'referral-input-container' },
                          React.createElement('input', {
                            type: 'text',
                            className: 'reference-input ' + (referralError ? 'error' : '') + (referralValid ? 'valid' : ''),
                            name: 'trans_code',
                            id: 'trans_code',
                            placeholder: 'Enter Referral Code *',
                            value: referenceCode,
                            onChange: handleReferralChange
                          }),
                          checkingReferral && React.createElement(
                            'span',
                            { className: 'referral-spinner' },
                            React.createElement('i', { className: 'fas fa-spinner fa-spin' })
                          ),
                          referralValid && React.createElement(
                            'span',
                            { className: 'referral-valid-icon' },
                            React.createElement('i', { className: 'fas fa-check-circle', style: { color: '#00b894' } })
                          ),
                          referralError && React.createElement(
                            'div',
                            { className: 'referral-error-message' },
                            referralError
                          ),
                          referralValid && referralUser && React.createElement(
                            'div',
                            { className: 'referral-success-message' },
                            React.createElement('i', { className: 'fas fa-check-circle' }),
                            referralUser.isUniversal ? 
                              ' 🌐 Universal Referral Code - Valid!' :
                              ' Referral code from ' + (referralUser.firstName || 'User') + ' ' + (referralUser.lastName || '') + ' - Valid!'
                          ),
                          !referenceCode && React.createElement(
                            'div',
                            { className: 'referral-required-message' },
                            React.createElement('i', { className: 'fas fa-asterisk', style: { color: '#ff6b6b' } }),
                            ' Referral code is required'
                          )
                        )
                      )
                    ),
                    React.createElement(
                      'tr',
                      null,
                      React.createElement('td', { className: 'summary-label' }, React.createElement('strong', null, 'Payment Mode')),
                      React.createElement(
                        'td',
                        { className: 'summary-value payment-modes' },
                        React.createElement(
                          'label',
                          { className: 'payment-option' },
                          React.createElement('b', null, 'PG 1'),
                          React.createElement('input', { 
                            type: 'radio', 
                            name: 'radio', 
                            value: '1',
                            checked: paymentMode === '1',
                            onChange: function(e) { setPaymentMode(e.target.value); }
                          })
                        ),
                        React.createElement(
                          'label',
                          { className: 'payment-option' },
                          React.createElement('b', null, 'PG 2'),
                          React.createElement('input', { 
                            type: 'radio', 
                            name: 'radio', 
                            value: '2',
                            checked: paymentMode === '2',
                            onChange: function(e) { setPaymentMode(e.target.value); }
                          })
                        ),
                        React.createElement(
                          'label',
                          { className: 'payment-option' },
                          React.createElement('b', null, 'PG 3'),
                          React.createElement('input', { 
                            type: 'radio', 
                            name: 'radio', 
                            value: '3',
                            checked: paymentMode === '3',
                            onChange: function(e) { setPaymentMode(e.target.value); }
                          })
                        )
                      )
                    )
                  )
                ),
                // Checkout Button
                React.createElement(
                  'div',
                  { className: 'cart-actions-row' },
                  React.createElement(
                    'button',
                    { 
                      className: 'btn-primary ' + (isCheckoutDisabled() ? 'disabled' : ''),
                      onClick: handleCheckout,
                      disabled: isCheckoutDisabled()
                    },
                    React.createElement('i', { className: 'fas fa-lock' }),
                    ' Proceed to Checkout'
                  ),
                  React.createElement(
                    'button',
                    { 
                      className: 'btn-secondary',
                      onClick: function() { handleNavigation('/'); }
                    },
                    React.createElement('i', { className: 'fas fa-shopping-bag' }),
                    ' Continue Shopping'
                  )
                ),
                // Warning messages
                (!referenceCode || referenceCode.trim() === '') && 
                  React.createElement(
                    'div',
                    { className: 'referral-blocked-message warning' },
                    React.createElement('i', { className: 'fas fa-exclamation-circle' }),
                    ' Please enter a referral code to proceed with checkout.'
                  ),
                referenceCode && !referralValid && !checkingReferral && 
                  React.createElement(
                    'div',
                    { className: 'referral-blocked-message error' },
                    React.createElement('i', { className: 'fas fa-exclamation-triangle' }),
                    ' Please enter a valid referral code to proceed with checkout.'
                  )
              )
            )
          )
      )
    )
  );
};

export default Cart;